import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import { db } from "@/db";
import { toPaise } from "@/utils/currency";

/**
 * @typedef {import('@/types/schema').Patient} Patient
 * @typedef {import('@/types/schema').User} User
 * @typedef {import('@/types/schema').CommissionRule} CommissionRule
 */

/**
 * Custom hook to handle facility_charge calculations and rule selection.
 * Encapsulates the logic for:
 * 1. Fetching applicable facility_charge rules
 * 2. Selecting the "best" rule (Specific Patient > General)
 * 3. Calculating the facility_charge amount based on the selected rule or legacy patient data
 * 
 * @param {string} doctorId - ID of the treating doctor
 * @param {string} patientId - ID of the selected patient
 * @param {string} clinicId - ID of the selected clinic
 * @param {string} feeAmount - Fee amount input (in Rupees)
 * @param {Patient} selectedPatient - The full patient object
 * @param {Array<User>} allDoctors - List of all doctors (to find referral info)
 * 
 * @returns {Object} Fee agreement logic results
 */
export function useFeeAgreementLogic(doctorId, patientId, clinicId, feeAmount, selectedPatient, allDoctors) {

    // Fetch applicable rules
    const applicableRules = useLiveQuery(async () => {
        if (!doctorId || !patientId) return [];

        // Get all rules for this doctor
        const rules = await db.fee_agreements.where('user_id').equals(doctorId).toArray();

        const isClinicAppt = clinicId && clinicId !== 'personal';
        const targetClinicId = isClinicAppt ? clinicId : null;

        // Filter logic mimicking backend
        return rules.filter(r => {
            // Status check
            if (r.status !== 'active') return false;

            // 1. Must match Patient if specified in rule
            if (r.patient_id && r.patient_id !== patientId) return false;

            // 2. Must match Clinic context
            if (r.clinic_id !== targetClinicId) return false;

            // If rule has no patient_id component, it applies to all patients in that context
            return true;
        });
    }, [doctorId, patientId, clinicId]) || [];

    const bestRule = useMemo(() => {
        if (!applicableRules.length) return null;

        // Sort: Specific Patient > General
        return applicableRules.sort((a, b) => {
            // If A has patient_id (specific) and B doesn't (general), A comes first
            if (a.patient_id && !b.patient_id) return -1;
            if (!a.patient_id && b.patient_id) return 1;
            return 0;
        })[0];
    }, [applicableRules]);

    const referralDoctor = useMemo(() => {
        // Fee agreements no longer have referral_doctor_id; fall back to patient's referral doctor
        return selectedPatient?.referral_doctor_id
            ? allDoctors.find(d => d.id === selectedPatient.referral_doctor_id)
            : null;
    }, [selectedPatient, allDoctors, bestRule]);

    const calculatedCharge = useMemo(() => {
        const fee = parseFloat(feeAmount) || 0;

        // 1. Use Rule if available
        if (bestRule) {
            if (bestRule.rule_type === 'percent') {
                return toPaise(fee) * bestRule.fee_value / 100;
            } else {
                // Flat facility_charge - bestRule.fee_value is already in paise
                return bestRule.fee_value;
            }
        }

        return 0;
    }, [feeAmount, bestRule]);

    return { bestRule, referralDoctor, calculatedCharge };
}
